import { useState, useEffect } from 'react';
import { templateLibAPI, aiEmailAPI } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Loader2, Plus, Trash2, ArrowLeft, Sparkles, Save, Edit } from 'lucide-react';
import { toast } from 'sonner';

function TemplateEditor({ templateId, onSaved, onBack }) {
  const [name, setName] = useState('');
  const [subject, setSubject] = useState('');
  const [preheader, setPreheader] = useState('');
  const [bodyHtml, setBodyHtml] = useState('');
  const [bodyText, setBodyText] = useState('');
  const [status, setStatus] = useState('draft');
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(!!templateId);
  const [aiSubjects, setAiSubjects] = useState([]);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiTone, setAiTone] = useState('professional');

  const isEdit = !!templateId;

  useEffect(() => {
    if (templateId) {
      templateLibAPI.get(templateId).then(r => {
        const t = r.data;
        setName(t.name || '');
        setSubject(t.subject || '');
        setPreheader(t.preheader || '');
        setBodyHtml(t.body_html || '');
        setBodyText(t.body_text || '');
        setStatus(t.status || 'draft');
        setLoading(false);
      }).catch(() => setLoading(false));
    }
  }, [templateId]);

  const handleSave = async () => {
    if (!name || !subject || !bodyHtml) return toast.error('Ad, konu ve icerik zorunlu');
    setSaving(true);
    try {
      if (isEdit) {
        await templateLibAPI.update(templateId, { name, subject, preheader, body_html: bodyHtml, body_text: bodyText, status });
        toast.success('Template guncellendi');
      } else {
        await templateLibAPI.save({ name, subject, preheader, body_html: bodyHtml, body_text: bodyText, status });
        toast.success('Template kaydedildi');
      }
      onSaved();
    } catch (e) { toast.error('Kaydedilemedi'); }
    setSaving(false);
  };

  const handleAISubjects = async () => {
    if (!bodyHtml) return toast.error('Once icerik yazin');
    setAiLoading(true);
    try {
      const res = await aiEmailAPI.generateSubjects(bodyHtml, aiTone, 10);
      setAiSubjects(res.data.subjects || []);
    } catch (e) { toast.error('AI hatasi'); }
    setAiLoading(false);
  };

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin" /></div>;

  return (
    <div className="max-w-3xl mx-auto space-y-6">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="icon" onClick={onBack}><ArrowLeft className="w-5 h-5" /></Button>
        <h1 className="text-2xl font-heading font-bold">{isEdit ? 'Template Duzenle' : 'Template Olustur'}</h1>
      </div>

      <Card><CardHeader><CardTitle>Temel Bilgiler</CardTitle></CardHeader><CardContent className="space-y-4">
        <div><Label>Template Adi</Label><Input value={name} onChange={e => setName(e.target.value)} placeholder="Orn: Oddsis Tanitim" /></div>
        <div><Label>Subject</Label><Input value={subject} onChange={e => setSubject(e.target.value)} placeholder="Email konusu" /></div>
        <div><Label>Preheader</Label><Input value={preheader} onChange={e => setPreheader(e.target.value)} placeholder="Inbox on yazi" /></div>
        <div><Label>Durum</Label>
          <div className="flex gap-1 mt-1">{['draft','active','archived'].map(s => <button key={s} onClick={() => setStatus(s)} className={`px-3 py-1 rounded text-xs font-medium ${status===s ? s==='active' ? 'bg-green-500 text-white' : s==='draft' ? 'bg-amber-500 text-white' : 'bg-muted' : 'bg-muted text-muted-foreground'}`}>{s==='draft'?'Taslak':s==='active'?'Aktif':'Arsiv'}</button>)}</div>
        </div>
      </CardContent></Card>

      <Card><CardHeader><CardTitle>Icerik</CardTitle></CardHeader><CardContent className="space-y-4">
        <div><Label>HTML Icerik</Label><textarea className="flex min-h-[200px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm font-mono" value={bodyHtml} onChange={e => setBodyHtml(e.target.value)} placeholder="<h1>Merhaba</h1>" /></div>
        <p className="text-xs text-muted-foreground">Placeholders: {'{{brand_name}}'}, {'{{domain_url}}'}, {'{{unsubscribe_url}}'}</p>
      </CardContent></Card>

      {/* AI Subject */}
      <Card><CardHeader><div className="flex items-center justify-between"><CardTitle className="flex items-center gap-2"><Sparkles className="w-4 h-4 text-purple-400" /> AI Subject</CardTitle><Button size="sm" onClick={handleAISubjects} disabled={aiLoading}>{aiLoading ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Sparkles className="w-3 h-3 mr-1" />} Oner</Button></div></CardHeader>
        <CardContent className="space-y-2">
          <div className="flex gap-1 flex-wrap">{[{k:'professional',l:'Profesyonel'},{k:'curious',l:'Merak'},{k:'urgent',l:'Acil'},{k:'campaign',l:'Kampanya'},{k:'short',l:'Kisa'}].map(t => <button key={t.k} onClick={() => setAiTone(t.k)} className={`px-2 py-0.5 rounded text-[10px] ${aiTone===t.k?'bg-primary text-primary-foreground':'bg-muted text-muted-foreground'}`}>{t.l}</button>)}</div>
          {aiSubjects.map((s, i) => <div key={i} onClick={() => setSubject(s.subject)} className="flex justify-between p-2 rounded hover:bg-muted/50 cursor-pointer text-sm"><span>{s.subject}</span><Badge variant="outline" className="text-[10px]">{s.score}</Badge></div>)}
        </CardContent>
      </Card>

      {/* Preview */}
      {bodyHtml && (
        <Card><CardHeader><CardTitle>Onizleme</CardTitle></CardHeader><CardContent>
          <div className="border border-border rounded-lg overflow-hidden">
            <div className="bg-muted/30 px-4 py-2 text-xs text-muted-foreground">Subject: {subject}</div>
            <div className="p-1" dangerouslySetInnerHTML={{ __html: bodyHtml.replace(/\{\{brand_name\}\}/g, 'Artemisbet').replace(/\{\{domain_url\}\}/g, 'https://artemisbetgirisitesi.com').replace(/\{\{unsubscribe_url\}\}/g, '#') }} />
          </div>
        </CardContent></Card>
      )}

      <Button className="w-full" size="lg" onClick={handleSave} disabled={saving}>{saving ? <Loader2 className="w-5 h-5 mr-2 animate-spin" /> : <Save className="w-5 h-5 mr-2" />} {isEdit ? 'Guncelle' : 'Kaydet'}</Button>
    </div>
  );
}

export default function TemplateBuilderPage() {
  const [view, setView] = useState('list');
  const [editId, setEditId] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => { try { const r = await templateLibAPI.list(); setTemplates(r.data); } catch(e){} setLoading(false); };
  useEffect(() => { load(); }, []);

  const handleDelete = async (id) => { await templateLibAPI.delete(id); toast.success('Silindi'); load(); };

  const handleToggleStatus = async (t) => {
    const newStatus = t.status === 'active' ? 'draft' : 'active';
    await templateLibAPI.update(t.id, { status: newStatus });
    toast.success(newStatus === 'active' ? 'Aktif edildi' : 'Taslaga alindi');
    load();
  };

  if (view === 'create') return <TemplateEditor onSaved={() => { setView('list'); load(); }} onBack={() => setView('list')} />;
  if (view === 'edit') return <TemplateEditor templateId={editId} onSaved={() => { setView('list'); setEditId(null); load(); }} onBack={() => { setView('list'); setEditId(null); }} />;

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin" /></div>;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div><h1 className="text-2xl font-heading font-bold">Template Builder</h1><p className="text-muted-foreground mt-1">Email sablonlari olusturun ve yonetin</p></div>
        <Button size="sm" onClick={() => setView('create')}><Plus className="w-4 h-4 mr-1" /> Template Olustur</Button>
      </div>
      {templates.length === 0 ? <Card><CardContent className="text-center py-16 text-muted-foreground">Henuz template yok</CardContent></Card> :
        <div className="space-y-3">{templates.map(t => (
          <Card key={t.id}><CardContent className="flex items-center justify-between py-4">
            <div className="flex-1">
              <div className="flex items-center gap-2">
                <p className="font-medium">{t.name}</p>
                <Badge variant={t.status === 'active' ? 'default' : t.status === 'draft' ? 'secondary' : 'outline'} className={t.status === 'active' ? 'bg-green-500/20 text-green-400' : ''}>{t.status === 'active' ? 'Aktif' : t.status === 'draft' ? 'Taslak' : 'Arsiv'}</Badge>
              </div>
              <p className="text-xs text-muted-foreground mt-1">Subject: {t.subject}</p>
            </div>
            <div className="flex gap-1">
              <Button variant="outline" size="sm" onClick={() => handleToggleStatus(t)}>{t.status === 'active' ? 'Taslaga Al' : 'Aktif Et'}</Button>
              <Button variant="outline" size="sm" onClick={() => { setEditId(t.id); setView('edit'); }}><Edit className="w-3 h-3 mr-1" /> Duzenle</Button>
              <Button variant="ghost" size="icon" className="text-destructive" onClick={() => handleDelete(t.id)}><Trash2 className="w-4 h-4" /></Button>
            </div>
          </CardContent></Card>
        ))}</div>
      }
    </div>
  );
}
